import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { formatCurrency, isCurrencyAmount, parseCurrencyAmount } from '@/lib/format';

const pluginKey = new PluginKey('investmentMoneyBlur');

function findCellInnerRange(
  view: EditorView,
  cellEl: HTMLElement
): { from: number; to: number } | null {
  try {
    const inner = cellEl.querySelector('p') ?? cellEl;
    const pos = view.posAtDOM(inner, 0);
    const $pos = view.state.doc.resolve(pos);
    for (let d = $pos.depth; d > 0; d--) {
      if ($pos.node(d).type.name === 'tableCell') {
        return {
          from: $pos.before(d) + 1,
          to: $pos.after(d) - 1,
        };
      }
    }
  } catch {
    return null;
  }
  return null;
}

function findInvestmentCurrency(view: EditorView, cellEl: HTMLElement): string {
  try {
    const pos = view.posAtDOM(cellEl, 0);
    const $pos = view.state.doc.resolve(pos);
    for (let d = $pos.depth; d >= 1; d--) {
      const n = $pos.node(d);
      if (n.type.name === 'investmentGroupNode') {
        return (n.attrs.currency as string) || 'EUR';
      }
    }
  } catch {
    // ignore
  }
  return 'EUR';
}

function markLinkedRowOverridden(view: EditorView, cellEl: HTMLElement) {
  try {
    const pos = view.posAtDOM(cellEl, 0);
    const $pos = view.state.doc.resolve(pos);
    for (let depth = $pos.depth; depth >= 1; depth--) {
      const node = $pos.node(depth);
      if (node.type.name !== 'tableRow') continue;
      if (!node.attrs.productId) return;
      if (node.attrs.hasOverrides) return;
      view.dispatch(
        view.state.tr.setNodeMarkup($pos.before(depth), undefined, {
          ...node.attrs,
          hasOverrides: true,
        })
      );
      return;
    }
  } catch {
    // De tekstinvoer blijft werken als de productkoppeling niet te vinden is.
  }
}

/**
 * Zet bij blur valuta-opmaak op prijs- en subtotaalcellen in investeringstabellen.
 */
export const InvestmentMoneyBlur = Extension.create({
  name: 'investmentMoneyBlur',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: pluginKey,
        props: {
          handleDOMEvents: {
            input: (view, event) => {
              const target = event.target as HTMLElement | null;
              const cell = target?.closest('td');
              if (!cell || !cell.closest('.investment-group-view')) return false;
              const row = cell.closest('tr');
              if (!row || Array.from(row.children).indexOf(cell) === 0) return false;
              markLinkedRowOverridden(view, cell);
              return false;
            },
            focusout: (view, event) => {
              const target = event.target as HTMLElement | null;
              if (!target) return false;
              const cell = target.closest('td');
              if (!cell || !cell.closest('.investment-group-view')) return false;

              const tr = cell.closest('tr');
              if (!tr) return false;
              const siblings = Array.from(tr.children);
              const colIdx = siblings.indexOf(cell as Element);
              const colCount = siblings.length;

              if (colCount === 1) return false;
              const isMoney =
                (colCount === 2 && colIdx === 1) ||
                (colCount === 4 && (colIdx === 1 || colIdx === 3));
              if (!isMoney) return false;

              const raw = cell.textContent?.trim() ?? '';
              if (!raw) return false;

              // Een prijscel mag ook vrije tekst bevatten (bijv. “Op maat” of
              // “N.V.T”). Alleen echte bedragen krijgen valuta-opmaak.
              if (!isCurrencyAmount(raw)) return false;

              const currency = findInvestmentCurrency(view, cell);
              const parsed = parseCurrencyAmount(raw);
              const formatted = formatCurrency(parsed, currency);
              const normalized = raw
                .replace(/\u00a0/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
              if (formatted === normalized) return false;

              const range = findCellInnerRange(view, cell);
              if (!range) return false;

              const { state } = view;
              const paragraph = state.schema.nodes.paragraph.create(
                null,
                state.schema.text(formatted)
              );
              const trEdit = state.tr.replaceWith(range.from, range.to, paragraph);
              view.dispatch(trEdit);
              return false;
            },
          },
        },
      }),
    ];
  },
});
